import React from 'react'

import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Flex, LoadingIcon } from '@cash-design-system/react'
import { isBusinessAccount } from '@cash-web/account-util-shared'
import { NameBlocker } from '@cash-web/protos/squareup/franklin/blockers/name_blocker.pb'
import { NameBlockerSupplement } from '@cash-web/protos/squareup/franklin/scenarios.pb'
import { useBlockerResponseHandler } from '@cash-web/shared-blocker-data-access-context'
import { useMutationSetFullName } from '@cash-web/shared-data-access-api/react-query/useMutationSetFullName'
import { createReactQueryHook } from '@cash-web/shared-data-access-api/types'
import { NameInput } from '@cash-web/shared-feature-form'
import { ModalErrorContent } from '@cash-web/shared-feature-modal'
import { useFlowContext } from '@cash-web/shared-flow-data-access-context'
import { post } from '@cash-web/shared-util-fetch'

import { getSetFullNameErrors } from '../../../util/statusErrors'
import { BaseBlocker, BaseBlockerFormFields, BlockerProps } from '../../BaseBlocker/BaseBlocker'

interface FormFields extends BaseBlockerFormFields {
  name: string
}

interface Props extends BlockerProps {
  blocker: NameBlocker
  supplement?: NameBlockerSupplement
  buttons?: React.ReactNode[]
}

const NameChangeNotAllowed = () => {
  const { t } = useTranslation()

  return (
    <ModalErrorContent
      title={t("Your name can't be changed", { context: 'title for unallowed customer name change' })}
      message={t(
        "You've reached the maximum number of name changes allowed. If you need it updated, you can contact support to request a change.",
        { context: 'message for unallowed customer name change' }
      )}
    />
  )
}

/**
  We are hardcoding this React Query hook, because the real proto for this API call
  is in Franklin's service.proto. This is a massive file that currently breaks our
  proto generation tool. We currently only need the below API, so hardcoding it instead.
 */
const getDisplayNameStatusPath = '/cash-app/community/get-display-name-status'
type GetDisplayNameStatusResponse = { remaining_limit: number }
const getDisplayNameStatus = () => post<object, GetDisplayNameStatusResponse>(getDisplayNameStatusPath)
export const useQueryGetDisplayNameStatus = createReactQueryHook('getDisplayNameStatus', getDisplayNameStatus)

export function BlockerName({ buttons, supplement }: Props): React.ReactElement {
  const form = useForm<FormFields>({ mode: 'onTouched' })
  const { t } = useTranslation()
  const flow = useFlowContext()
  const { handleBlockerResponse, genericError } = useBlockerResponseHandler()
  const { mutateAsync: setFullName } = useMutationSetFullName({
    options: {
      clientScenario: flow.active?.clientScenario,
      flowToken: flow.active?.flowToken,
    },
  })
  const { data: displayNameStatus, isLoading: displayNameStatusIsLoading } = useQueryGetDisplayNameStatus({})

  const onSubmit = async ({ name }: FormFields) => {
    await handleBlockerResponse({
      promisedBlockerUpdate: setFullName({
        full_name: name.trim(),
        transfer_token: flow.active?.transferToken,
        payment_tokens: flow.active?.paymentTokens,
      }),
      errors: getSetFullNameErrors(genericError, t),
    })
  }

  const title = isBusinessAccount(supplement?.rate_plan)
    ? t('Edit your business name', { context: 'Title text.' })
    : t('Edit your full name', { context: 'Title text.' })
  const subtitle = isBusinessAccount(supplement?.rate_plan)
    ? ''
    : t('This helps confirm that you’re really you.', { context: 'Subtitle text.' })
  const label = isBusinessAccount(supplement?.rate_plan)
    ? t('Business name', { context: 'Label text.' })
    : t('Full name', { context: 'Label text.' })

  const mainText = title ?? t('What’s your name?', { context: 'Title text.' })
  const secondaryText = subtitle ?? t('This helps confirm that you’re really you.', { context: 'Subtitle text.' })

  const defaultValue = supplement?.name_prefill ?? ''

  const helpText = t('You have {limit} name changes remaining', {
    limit: displayNameStatus?.remaining_limit,
    context: 'remaining times a customer can change their name',
  })

  if (displayNameStatusIsLoading) {
    return (
      <Flex justifyContent="center">
        <LoadingIcon />
      </Flex>
    )
  }

  if (displayNameStatus?.remaining_limit === 0) {
    return <NameChangeNotAllowed />
  }

  return (
    <BaseBlocker mainText={mainText} secondaryText={secondaryText} form={form} onSubmit={onSubmit} buttons={buttons}>
      <NameInput autoFocus name="name" defaultValue={defaultValue} label={label} helpText={helpText} />
    </BaseBlocker>
  )
}
